import React, { ReactNode, useEffect } from "react";
import Image from "next/image";
import { cn } from "@/shared/utils";
import Strings from "@/shared/utils/Strings.constants";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { PolicyCalloutFrame } from "./PolicyCalloutFrame";
import { faShieldDog, faSlidersUp, faBadgePercent, IconDefinition, faPaw } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Perk logos
import Rover from "@/shared/components/PolicyCalloutTest/assets/rover.webp";
import Furbo from "@/shared/components/PolicyCalloutTest/assets/furbo.webp";
import Fi from "@/shared/components/PolicyCalloutTest/assets/fi.webp";
import Basepaws from "@/shared/components/PolicyCalloutTest/assets/basepaws.webp";
import DogTV from "@/shared/components/PolicyCalloutTest/assets/DogTV.webp";
import Halo from "@/shared/components/PolicyCalloutTest/assets/halo.webp";
import Maven from "@/shared/components/PolicyCalloutTest/assets/maven.webp";

// Icons

export type PolicyCalloutVariant = "whySpot";

export type SimplifiVariant = "lifestyle" | "reassurance";

interface VariantPetStepHeroProps {
    variant: PolicyCalloutVariant;
    simplifiVariant?: SimplifiVariant;
    bottomSlot?: ReactNode;
}

interface WhySpotBlurbProps {
    icon: ReactNode;
    heading: string;
    description: string;
    reference?: string;
}

const PERK_IMAGE_BASE = "w-fit rounded-lg";

const WhySpotBlurb: React.FC<WhySpotBlurbProps> = ({ icon, heading, description, reference }) => {
    return (
        <div className="flex items-center gap-2 xs:gap-3 lg:gap-2 xl:gap-3">
            {icon}
            <div>
                <p className="text-sm font-bold text-content-primary sm:text-base md:text-lg lg:text-sm xl:text-base">{heading}</p>
                <p className="text-xs text-content-secondary sm:text-sm md:text-base lg:text-xs xl:text-sm">
                    {description}
                    {reference && <sup>{reference}</sup>}
                </p>
            </div>
        </div>
    );
};

type BlurbContent = Omit<WhySpotBlurbProps, "icon"> & { icon: IconDefinition };

const WHY_SPOT_BLURBS: Record<"default" | SimplifiVariant, BlurbContent[]> = {
    default: [
        { icon: faSlidersUp, heading: "Great flexibility", description: "Customize coverage w/ preventive care add-ons" },
        { icon: faShieldDog, heading: "Extensive coverage", description: "Exam fees, prescription foods, and more" },
        { icon: faBadgePercent, heading: "$2,500", description: "in special perks and discounts", reference: "2" }
    ],
    reassurance: [
        { icon: faSlidersUp, heading: "Great flexibility", description: "Customize coverage w/ preventive care add-ons" },
        { icon: faShieldDog, heading: "Extensive coverage", description: "Exam fees, prescriptions, surgeries, and more" },
        { icon: faBadgePercent, heading: "$2,500", description: "in special perks and discounts", reference: "2" }
    ],
    lifestyle: [
        { icon: faPaw, heading: "For active lives", description: 'Sprains, scrapes, and "how did they swallow that?"' },
        { icon: faShieldDog, heading: "Routine to unexpected", description: "Options for annual checkups to surprise surgeries, covered" },
        { icon: faBadgePercent, heading: "$2,500 in perks", description: "Discounts on the brands you already love", reference: "2" }
    ]
};

const WhySpotContent: React.FC<{ simplifiVariant?: SimplifiVariant }> = ({ simplifiVariant }) => {
    const ICON_CLASSES = "size-6 sm:size-8 md:size-10 lg:size-6 xl:size-8 text-content-brand-b";
    const blurbs = WHY_SPOT_BLURBS[simplifiVariant ?? "default"];

    return (
        <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-1 lg:gap-3 xl:grid-cols-2 xl:gap-4">
                {blurbs.map(({ icon, ...blurb }) => (
                    <WhySpotBlurb key={blurb.heading} icon={<FontAwesomeIcon icon={icon} className={cn(ICON_CLASSES)} />} {...blurb} />
                ))}
            </div>
            <div className="grid grid-cols-4 items-center gap-2 xs:grid-cols-5 sm:grid-cols-7 lg:grid-cols-5 xl:grid-cols-6">
                <Image priority src={Halo} alt="Halo" className={PERK_IMAGE_BASE} />
                <Image priority src={Fi} alt="Fi" className={PERK_IMAGE_BASE} />
                <Image priority src={Rover} alt="Rover" className={PERK_IMAGE_BASE} />
                <Image priority src={Furbo} alt="Furbo" className={PERK_IMAGE_BASE} />
                <Image priority src={Basepaws} alt="Basepaws" className={cn(PERK_IMAGE_BASE, "hidden xs:block")} />
                <Image priority src={Maven} alt="Maven" className={cn(PERK_IMAGE_BASE, "hidden sm:block lg:hidden xl:block")} />
                <Image priority src={DogTV} alt="Dog TV" className={cn(PERK_IMAGE_BASE, "hidden sm:block lg:hidden xl:hidden")} />
            </div>
        </>
    );
};

export default function VariantPetStepHero({ variant, simplifiVariant, bottomSlot }: VariantPetStepHeroProps) {
    const { updateAppState } = useAppLayerContext();
    const CALLOUT_CONTENT = Strings.POLICY_CALLOUT_CONTENT;
    const isWhySpot = variant === "whySpot";
    const isSimplifi = Boolean(simplifiVariant);
    const title =
        simplifiVariant === "lifestyle"
            ? CALLOUT_CONTENT.titles.simplifiLifestyle
            : simplifiVariant === "reassurance"
              ? CALLOUT_CONTENT.titles.simplifiReassurance
              : CALLOUT_CONTENT.titles.whySpot;
    const tagline = isSimplifi ? CALLOUT_CONTENT.simplifiTagline : CALLOUT_CONTENT.tagline;
    const reassuranceSubheading = CALLOUT_CONTENT.subheadings.simplifiReassurance;
    const subheading: ReactNode =
        simplifiVariant === "lifestyle" ? (
            CALLOUT_CONTENT.subheadings.simplifiLifestyle
        ) : simplifiVariant === "reassurance" ? (
            <>
                {reassuranceSubheading.prefix}
                <strong className="font-bold">{reassuranceSubheading.bold}</strong>
                {reassuranceSubheading.suffix}
            </>
        ) : undefined;

    useEffect(() => {
        updateAppState({ isRetargetingFormCopyControl: isWhySpot });
        return () => {
            updateAppState({ isRetargetingFormCopyControl: undefined });
        };
    }, [isWhySpot, updateAppState]);

    return (
        <PolicyCalloutFrame
            title={title}
            tagline={isWhySpot ? tagline : undefined}
            subheading={subheading}
            bottomSlot={bottomSlot}
            showAccolades={true}
            accoladesVariant="whySpot"
            showWhySpotBox={isWhySpot}
            containerClassName={cn(isWhySpot && "-mb-8 sm:mb-0")}
            titleClassName={cn(isWhySpot && "lg:text-2xl lg:font-bold xl:text-start xl:text-3xl")}
            contentClassName="px-4 pb-4 pt-6" // Why spot box padding
        >
            {isWhySpot && <WhySpotContent simplifiVariant={simplifiVariant} />}
        </PolicyCalloutFrame>
    );
}
